'use client';

import { motion } from 'framer-motion';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { ChevronDown, Zap } from 'lucide-react';
import { Card } from '@/components/common/Card';

export interface FAQItem {
  question: string;
  answer: string;
}

export interface FAQCategory {
  title: string;
  faqs: FAQItem[];
}

interface FAQProps {
  categories: FAQCategory[];
  className?: string;
  variant?: 'grid' | 'single-column';
}

export function FAQ({
  categories,
  className = '',
  variant = 'grid',
}: FAQProps) {
  const formatAnswer = (answer: string) => {
    // Split by newlines and filter out empty lines
    const lines = answer.split('\n').filter((line) => line.trim());

    // If the answer contains bullet points, format them
    if (lines.some((line) => line.trim().startsWith('-'))) {
      return (
        <ul className="list-none space-y-3">
          {lines.map((line, index) => {
            const trimmedLine = line.trim();
            if (trimmedLine.startsWith('-')) {
              return (
                <li key={index} className="flex items-start">
                  <span className="mr-3 mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary-500/80" />
                  <span className="text-gray-700">
                    {trimmedLine.substring(1).trim()}
                  </span>
                </li>
              );
            }
            return (
              <p key={index} className="mb-3 text-gray-700">
                {trimmedLine}
              </p>
            );
          })}
        </ul>
      );
    }

    // If the answer contains numbered points (e.g., "1) Point")
    if (lines.some((line) => /^\d+\)/.test(line.trim()))) {
      return (
        <ul className="list-none space-y-3">
          {lines.map((line, index) => {
            const trimmedLine = line.trim();
            if (/^\d+\)/.test(trimmedLine)) {
              return (
                <li key={index} className="flex items-start">
                  <span className="mr-3 mt-1.5 h-2 w-2 flex-shrink-0 rounded-full bg-primary-500/80" />
                  <span className="text-gray-700">
                    {trimmedLine.substring(trimmedLine.indexOf(')') + 1).trim()}
                  </span>
                </li>
              );
            }
            return (
              <p key={index} className="mb-3 text-gray-700">
                {trimmedLine}
              </p>
            );
          })}
        </ul>
      );
    }

    // Default formatting with paragraphs
    return lines.map((line, index) => (
      <p key={index} className="mb-3 text-gray-700">
        {line.trim()}
      </p>
    ));
  };

  return (
    <div className={className}>
      <div
        className={
          variant === 'grid'
            ? 'grid gap-8 md:grid-cols-2 lg:grid-cols-2'
            : 'space-y-8'
        }
      >
        {categories.map((category, categoryIndex) => (
          <motion.div
            key={category.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: categoryIndex * 0.1 }}
          >
            <Card className="h-full">
              <div className="mb-6 border-b border-gray-100 bg-gradient-to-r from-primary-50 to-primary-100/30 px-4 py-2">
                <h2 className="text-2xl font-bold text-gray-900">
                  {category.title}
                </h2>
              </div>
              <div className="space-y-4">
                {category.faqs.map((faq, faqIndex) => (
                  <Disclosure
                    key={faqIndex}
                    as="div"
                    className="overflow-hidden rounded-lg bg-white shadow-sm transition-all duration-200 hover:shadow-md"
                  >
                    {({ open }) => (
                      <>
                        <DisclosureButton className="flex w-full items-center justify-between rounded-lg bg-white px-5 py-4 text-left font-medium text-gray-900 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring focus-visible:ring-primary-500 focus-visible:ring-opacity-50">
                          <span className="flex items-center text-lg">
                            <Zap
                              className={`mr-3 h-5 w-5 text-primary-500 transition-transform ${
                                open ? 'rotate-12' : ''
                              }`}
                            />
                            {faq.question}
                          </span>
                          <ChevronDown
                            className={`${
                              open ? 'rotate-180 transform' : ''
                            } h-5 w-5 text-gray-500 transition-transform`}
                          />
                        </DisclosureButton>
                        <DisclosurePanel className="p-4 text-base">
                          {formatAnswer(faq.answer)}
                        </DisclosurePanel>
                      </>
                    )}
                  </Disclosure>
                ))}
              </div>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
